import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink, Code, Smartphone, TrendingUp, Shield, Zap, Globe, GraduationCap, ArrowRight, Star, Trophy } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

import { BACKEND_URL } from '../utils/api';

/**
 * Frontend-guaranteed portfolio entries.
 * These ALWAYS render — even when the /api/portfolio MongoDB collection
 * is empty (which can happen on a fresh deploy, after a DB wipe, or in
 * preview environments that don't share data with production Atlas).
 *
 * The fetch from /api/portfolio still runs and dynamically-managed entries
 * (like contest winners) will show alongside these. Dedupe is done by URL
 * so an admin-added DB entry overrides the hardcoded version.
 */
const STATIC_PROJECTS = [
  {
    id: 'static-charityfundraiser',
    name: 'CharityFundraiser.com',
    url: 'https://charityfundraiser.com',
    category: 'Nonprofit Fundraising Platform',
    category_es: 'Plataforma de Recaudación para Organizaciones sin Fines de Lucro',
    description: 'A Web-TechServices product. The most advanced all-in-one AI fundraising platform for nonprofits, schools, PTAs, churches, sports teams, and individual fundraisers. Silent + live auctions, donations, peer-to-peer, ticketing, raffles, golf tournaments, donor CRM, volunteer management, AI campaign coaching — one dashboard. Flat 2% — no donor tip nag, no contracts. Check in 400 guests in 9 minutes.',
    description_es: 'Un producto de Web-TechServices. La plataforma de recaudación todo-en-uno más avanzada con IA — para organizaciones sin fines de lucro, escuelas, iglesias y equipos deportivos. Subastas silenciosas y en vivo, donaciones, peer-to-peer, boletos, rifas, torneos de golf, CRM de donantes y coaching de campañas con IA — todo en un solo panel. Tarifa plana del 2% — sin avisos de propina al donante, sin contratos. Registra a 400 invitados en 9 minutos.',
    screenshot_url: '/images/portfolio/charityfundraiser.jpg',
    features: ['12 fundraising tools in one dashboard', 'AI campaign copy + smart-pricing + auctioneer talking points', 'Volunteer efficiency reports (industry-first)', 'QR-tag flow: 400 guests in 9 minutes', 'Flat 2% vs Givebutter 3% / OneCause $5K-$25K/yr', 'Stripe Connect direct payout · PayPal · Apple/Google Pay · ACH'],
    features_es: ['12 herramientas de recaudación en un panel', 'Texto de campaña con IA + precios inteligentes + guion para subastador', 'Reportes de eficiencia de voluntarios (primero en la industria)', 'Flujo con códigos QR: 400 invitados en 9 minutos', 'Tarifa plana del 2% vs Givebutter 3% / OneCause $5K-$25K/año', 'Stripe Connect pago directo · PayPal · Apple/Google Pay · ACH'],
    results: 'Live in production. Replaces Givebutter, OneCause, BiddingForGood, and DonorPerfect with one unified platform — used by nonprofits coast-to-coast.',
    results_es: 'En producción. Reemplaza Givebutter, OneCause, BiddingForGood y DonorPerfect con una sola plataforma unificada — usada por organizaciones sin fines de lucro en todo EE. UU.',
    promo_text: 'Are you a nonprofit, school, or church running fundraising events? Try our flagship product free — no setup, no monthly, no contracts. charityfundraiser.com',
    promo_text_es: '¿Es usted una organización sin fines de lucro, escuela o iglesia que organiza eventos de recaudación? Pruebe nuestro producto principal gratis — sin configuración, sin mensualidad, sin contratos. charityfundraiser.com',
    promo_badge: 'JUST LAUNCHED',
    promo_badge_es: 'RECIÉN LANZADO',
    featured: true,
    sort_order: 1,
  },
  {
    id: 'static-duchenerealty',
    name: 'DuChene Realty LLC',
    url: 'https://duchenerealty.com',
    category: 'Real Estate',
    category_es: 'Bienes Raíces',
    description: 'Custom real estate brokerage website for DuChene Realty LLC, serving the DFW Metroplex including Arlington, Mansfield, Grand Prairie, Southlake, Colleyville and surrounding Texas communities. IDX-architected and geo-targeted to every service city.',
    description_es: 'Sitio web personalizado de corretaje de bienes raíces para DuChene Realty LLC, atendiendo el área metropolitana de DFW incluyendo Arlington, Mansfield, Grand Prairie, Southlake, Colleyville y comunidades cercanas de Texas. Arquitectura IDX y geo-segmentada a cada ciudad de servicio.',
    screenshot_url: '/images/portfolio/duchenerealty.jpg',
    features: ['Custom React + FastAPI', 'DFW Geo-Targeted', 'MLS / IDX Ready', 'Lead Capture', 'Mobile-First'],
    features_es: ['React + FastAPI a Medida', 'Geo-Segmentación DFW', 'Listo para MLS / IDX', 'Captura de Leads', 'Primero Móvil'],
    results: 'Live in production with full SEO coverage across 8+ DFW cities.',
    results_es: 'En producción con cobertura SEO completa en más de 8 ciudades de DFW.',
    featured: true,
    sort_order: 2,
  },
  {
    id: 'static-prosperdoorrepair',
    name: 'Prosper Garage Door Repair',
    url: 'https://prosperdoorrepair.com',
    category: 'Local Home Services',
    category_es: 'Servicios para el Hogar',
    description: 'Custom local-service website for a 24/7 garage door repair company serving Prosper, TX and Collin County. Conversion-focused and mobile-first, with instant quote capture, click-to-call, and geo-targeted service-area pages built to win emergency jobs the moment a homeowner searches.',
    description_es: 'Sitio web de servicio local personalizado para una empresa de reparación de puertas de garaje 24/7 que atiende Prosper, TX y el condado de Collin. Enfocado en conversión y primero móvil, con captura instantánea de cotizaciones, clic-para-llamar y páginas de área de servicio geo-segmentadas, diseñado para ganar trabajos de emergencia en el momento en que un cliente busca.',
    screenshot_url: '/images/portfolio/prosperdoorrepair.jpg',
    features: ['Custom Build', 'Local SEO', 'Instant Quote Form', 'Click-to-Call', 'Service-Area Pages', 'Mobile-First'],
    features_es: ['Construcción a Medida', 'SEO Local', 'Formulario de Cotización', 'Clic para Llamar', 'Páginas de Área de Servicio', 'Primero Móvil'],
    featured: true,
    sort_order: 3,
  },
  {
    id: 'static-sierrascala',
    name: 'SierraScala.com',
    url: 'https://sierrascala.com',
    category: 'Personal Portfolio',
    category_es: 'Portafolio Personal',
    description: 'Personal branding portfolio for a DFW-based recent graduate. Stunning dark theme with animated sections, interactive project showcases, skills visualization, and a contact form. Built to help new graduates and young professionals stand out in a competitive job market.',
    description_es: 'Portafolio de marca personal para una recién graduada de DFW. Tema oscuro impactante con secciones animadas, proyectos interactivos, visualización de habilidades y formulario de contacto. Diseñado para que recién graduados y jóvenes profesionales destaquen en un mercado laboral competitivo.',
    screenshot_url: '/images/portfolio/sierrascala.jpg',
    features: ['Personal Branding', 'Portfolio', 'Dark Theme', 'Animations', 'Contact Form', 'Mobile-First', 'SEO Optimized'],
    features_es: ['Marca Personal', 'Portafolio', 'Tema Oscuro', 'Animaciones', 'Formulario de Contacto', 'Primero Móvil', 'Optimizado SEO'],
    results: 'Professional online presence, Google-indexed in 48 hours, 2x interview callback rate.',
    results_es: 'Presencia profesional en línea, indexada en Google en 48 horas, 2x la tasa de retorno de entrevistas.',
    promo_text: 'Know a recent graduate? We\u2019ll build them a personal portfolio site. Ask us about our Graduate Launch Program.',
    promo_text_es: '¿Conoces a un recién graduado? Le construimos un sitio de portafolio personal. Pregúntanos por nuestro Programa de Lanzamiento para Graduados.',
    promo_badge: 'NEW',
    promo_badge_es: 'NUEVO',
    featured: true,
    sort_order: 4,
  },
];

/** Merge static + API projects, deduped by URL (API wins). */
const mergeProjects = (staticList, apiList) => {
  const byUrl = new Map();
  staticList.forEach((p) => byUrl.set((p.url || '').toLowerCase().replace(/\/$/, ''), p));
  apiList.forEach((p) => byUrl.set((p.url || '').toLowerCase().replace(/\/$/, ''), p));
  return [...byUrl.values()].sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
};

const PortfolioShowcase = () => {
  const location = useLocation();
  const es = location.pathname.startsWith('/es');
  const [projects, setProjects] = useState(STATIC_PROJECTS);
  const [loading, setLoading] = useState(true);
  const [freshShots, setFreshShots] = useState({}); // normalized url -> updated_at (auto-refreshed screenshots)

  useEffect(() => {
    fetchProjects();
    fetchShots();
  }, []);

  const normUrl = (u) => (u || '').toLowerCase().replace(/\/$/, '');

  const fetchShots = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/portfolio-screenshots`);
      const data = await res.json();
      const map = {};
      (data.screenshots || []).forEach((s) => { if (s.norm) map[s.norm] = s.updated_at || '1'; });
      setFreshShots(map);
    } catch (e) { /* fall back to bundled screenshots */ }
  };

  // Effective screenshot src: a fresh auto-captured shot (served via the backend
  // proxy) when available, otherwise the bundled static image.
  const shotSrc = (project) => {
    const n = normUrl(project.url);
    if (freshShots[n]) {
      const v = encodeURIComponent(String(freshShots[n]));
      return `${BACKEND_URL}/api/portfolio-screenshot?url=${encodeURIComponent(project.url)}&v=${v}`;
    }
    return project.screenshot_url;
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/portfolio`);
      const data = await response.json();
      setProjects(mergeProjects(STATIC_PROJECTS, data.projects || []));
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      // Fall back to static-only on network failure
      setProjects(STATIC_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      'frontend': <Code className="h-3.5 w-3.5" />,
      'backend': <Code className="h-3.5 w-3.5" />,
      'mobile': <Smartphone className="h-3.5 w-3.5" />,
      'mobile-first': <Smartphone className="h-3.5 w-3.5" />,
      'seo': <TrendingUp className="h-3.5 w-3.5" />,
      'seo optimized': <TrendingUp className="h-3.5 w-3.5" />,
      'security': <Shield className="h-3.5 w-3.5" />,
      'performance': <Zap className="h-3.5 w-3.5" />,
      'animations': <Zap className="h-3.5 w-3.5" />,
      'personal branding': <Star className="h-3.5 w-3.5" />,
      'portfolio': <Globe className="h-3.5 w-3.5" />,
    };
    return icons[feature.toLowerCase()] || <Code className="h-3.5 w-3.5" />;
  };

  if (loading) {
    return (
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </section>
    );
  }

  if (projects.length === 0) return null;

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-slate-50 to-blue-50" id="portfolio">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {es ? 'Nuestro Trabajo Habla por Sí Solo' : 'Our Work Speaks for Itself'}
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {es ? 'Desde plataformas FSBO hasta portafolios de marca personal y herramientas potenciadas por IA — construimos soluciones que generan resultados reales.' : 'From FSBO platforms to personal branding portfolios to AI-powered tools — we build solutions that drive real results.'}
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <Card key={project.id} className="hover:shadow-2xl transition-all duration-300 group overflow-hidden"
              data-testid={`portfolio-${project.id}`}>
              {/* Screenshot */}
              {project.screenshot_url && (
                <div className="relative overflow-hidden bg-slate-900 aspect-video">
                  <img 
                    src={shotSrc(project)} 
                    alt={`${project.name} screenshot`}
                    data-static={project.screenshot_url}
                    loading="lazy"
                    onError={(e) => {
                      const fb = e.currentTarget.getAttribute('data-static');
                      if (fb && !e.currentTarget.dataset.fellback && e.currentTarget.src.indexOf(fb) === -1) {
                        e.currentTarget.dataset.fellback = '1';
                        e.currentTarget.src = fb;
                      } else {
                        e.currentTarget.style.display = 'none';
                      }
                    }}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <a href={project.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-white font-semibold hover:underline">
                        {es ? 'Visitar Sitio' : 'Visit Live Site'} <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                  {/* Promo Badge */}
                  {project.promo_badge && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-emerald-500 text-white font-bold px-3 py-1 text-xs shadow-lg animate-pulse">
                        {(es && project.promo_badge_es) || project.promo_badge}
                      </Badge>
                    </div>
                  )}
                  {/* Contest Winner ribbon — make the $50K giveaway winner pop */}
                  {(project.contest_winner || project.id === 'overhaul-ops') && (
                    <div className="absolute top-0 left-0" data-testid="contest-winner-ribbon">
                      <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-500 text-amber-950 font-extrabold text-xs pl-3 pr-4 py-1.5 rounded-br-2xl shadow-lg">
                        <Trophy className="h-4 w-4" />
                        {es ? 'GANADOR $50K' : '$50K GRAND PRIZE WINNER'}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <CardContent className="p-6">
                {/* Contest Winner Badge */}
                {project.featured && project.id === 'overhaul-ops' && (
                  <div className="mb-3">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold px-3 py-1">
                      {es ? 'Ganador del Gran Premio del Concurso $50K' : '$50K Contest Grand Prize Winner'}
                    </Badge>
                  </div>
                )}
                
                {/* Project Name & URL */}
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{project.name}</h3>
                  <a href={project.url} target="_blank" rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline text-sm flex items-center gap-1">
                    {project.url.replace('https://', '')} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>

                {/* Description */}
                <p className="text-slate-600 mb-4">{(es && project.description_es) || project.description}</p>

                {/* Features */}
                {project.features && project.features.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1.5">
                      {((es && project.features_es) || project.features).map((feature, idx) => (
                        <Badge key={idx} variant="outline" className="flex items-center gap-1 text-xs">
                          {getFeatureIcon(feature)}
                          <span>{feature}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Results */}
                {project.results && (
                  <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded mb-4">
                    <p className="text-sm font-semibold text-green-900">{(es && project.results_es) || project.results}</p>
                  </div>
                )}

                {/* Promo Text (Graduate offer etc.) */}
                {project.promo_text && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 p-3 rounded-lg mb-4">
                    <p className="text-sm text-indigo-800 flex items-start gap-2">
                      <GraduationCap className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-600" />
                      {(es && project.promo_text_es) || project.promo_text}
                    </p>
                  </div>
                )}
                
                {/* Visit Button */}
                <a href={project.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
                  <Globe className="h-4 w-4" />
                  {es ? 'Visitar' : 'Visit'} {project.url.replace('https://', '')}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Graduate Launch Program CTA */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-blue-700 rounded-2xl p-8 md:p-10 text-white relative overflow-hidden"
          data-testid="graduate-promo">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4"></div>
          <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="h-8 w-8 md:h-10 md:w-10 text-white" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{es ? 'Programa de Lanzamiento para Graduados' : 'Graduate Launch Program'}</h3>
              <p className="text-blue-100 text-base md:text-lg mb-1">
                {es ? '¿Conoces a un graduado reciente comenzando su carrera? Dale una ventaja con un sitio web de portafolio profesional.' : 'Know a recent graduate starting their career? Give them a head start with a professional portfolio website.'}
              </p>
              <ul className="text-blue-200 text-sm space-y-1 mt-3 mb-4">
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <Star className="h-3.5 w-3.5 text-yellow-300" /> {es ? 'Sitio web de marca personal como sierrascala.com' : 'Custom personal brand website like sierrascala.com'}
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <Star className="h-3.5 w-3.5 text-yellow-300" /> {es ? 'Optimizado para SEO para que los reclutadores los encuentren en Google' : 'SEO-optimized so recruiters find them on Google'}
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <Star className="h-3.5 w-3.5 text-yellow-300" /> {es ? 'Diseño móvil-responsivo con animaciones modernas' : 'Mobile-responsive with modern animations'}
                </li>
                <li className="flex items-center gap-2 justify-center md:justify-start">
                  <Star className="h-3.5 w-3.5 text-yellow-300" /> {es ? 'Configuración de dominio personalizado incluida' : 'Custom domain setup included'}
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-3 flex-shrink-0">
              <a href={es ? '/es/contacto' : '/consultation'}>
                <Button className="bg-white text-indigo-700 hover:bg-blue-50 font-bold px-6 h-11 text-sm shadow-lg w-full"
                  data-testid="graduate-cta">
                  {es ? 'Regalar un Sitio de Portafolio' : 'Gift a Portfolio Site'} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </a>
              <a href="https://sierrascala.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 h-10 text-sm w-full">
                  {es ? 'Ver Ejemplo' : 'See Example'} <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* General CTA */}
        <div className="text-center mt-10">
          <p className="text-slate-600 mb-6">
            {es ? '¿Listo para ver lo que podemos construir para tu negocio?' : 'Ready to see what we can build for your business?'}
          </p>
          <a href={es ? '/es/contacto' : '/book-consultation'}>
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              {es ? 'Obtén tu Consulta Gratis' : 'Get Your Free Consultation'}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default PortfolioShowcase;
